import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../styles/RefundRequest.css';
import { getPaymentDetail, submitRefundRequest } from '../utils/api';
import Dialog from '../components/Dialog';

const RefundRequest = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [refundReason, setRefundReason] = useState('');
  const [customReason, setCustomReason] = useState(''); // 직접 입력한 사유
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [selectedOption, setSelectedOption] = useState('기능 부족');
  const [paymentData, setPaymentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // 다이얼로그 관련 상태
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [dialogMessage, setDialogMessage] = useState('');
  
  console.log("RefundRequest 컴포넌트가 렌더링됨, id:", id); // 디버깅용 로그
  
  // API에서 결제 정보 가져오기
  useEffect(() => {
    const fetchPaymentDetail = async () => {
      try {
        setLoading(true);
        setError(null);
        
        // API에서 결제 상세 정보 가져오기 (환불 요청용 파라미터 추가)
        const response = await getPaymentDetail(id, { isRefund: true });
        console.log('결제 상세 응답:', response);
        
        if (response && response.status === 'success' && response.data) {
          // 결제 정보 포맷팅
          const data = response.data;
          
          // 날짜 포맷 변환
          const dateObj = new Date(data.date);
          const purchaseDate = `${dateObj.getFullYear()}년 ${dateObj.getMonth() + 1}월 ${dateObj.getDate()}일`;
          
          // 금액 포맷팅
          const formattedAmount = `₩ ${data.amount.toLocaleString()}`;
          
          // 환불 금액 포맷팅 
          const formattedRefundAmount = data.refund_info.refund_amount 
            ? `₩ ${data.refund_info.refund_amount.toLocaleString()}`
            : 'formattedAmount'; // 환불 금액이 없으면 원래 금액을 사용
          
          // 카드 정보 포맷팅
          let cardInfo = data.payment_method;
          if (data.detail && data.detail.card) {
            const cardCompany = data.detail.card.company || "";
            const cardNumber = data.detail.card.number || "";
            
            // 카드 회사명이 비어있으면 '카드'로 표시
            cardInfo = `${cardCompany ? cardCompany : '카드'} ${cardNumber}`;
          }
          
          // 결제 데이터 설정
          const formattedPaymentData = {
            orderId: data.id || '',
            purchaseDate: purchaseDate,
            plan: data.plan || 'TapGo Plus 플랜',
            amount: formattedAmount,
            refundAmount: formattedRefundAmount,
            paymentMethod: cardInfo,
            refundDate: data.refund_info
          };
          
          console.log('포맷팅된 결제 데이터:', formattedPaymentData);
          setPaymentData(formattedPaymentData);
        } else {
          setError('결제 정보를 불러올 수 없습니다.');
          console.error('결제 정보 로드 실패:', response);
        }
        
        setLoading(false);
      } catch (err) {
        console.error('결제 상세 정보 로딩 오류:', err);
        setError('결제 정보를 불러오는 중 오류가 발생했습니다: ' + err.message);
        setLoading(false);
      }
    };
    
    if (id) {
      fetchPaymentDetail();
    } else {
      setError('유효한 결제 ID가 없습니다.');
      setLoading(false);
    }
  }, [id]);

  // 기본 데모 데이터 (API 호출 실패시 대체용)
  const defaultPaymentData = {
    orderId: '968416061615',
    purchaseDate: '2025년 9월 20일',
    plan: 'TapGo Plus 1개월 플랜',
    amount: '₩ 20,000',
    paymentMethod: '신한카드 1234******7890',
  };

  // 현재 사용할 결제 데이터 (API 데이터가 있으면 사용, 없으면 기본 데모 데이터 사용)
  const currentPaymentData = paymentData || defaultPaymentData;

  // 환불 사유 옵션 리스트
  const reasonOptions = [
    { value: 'feature_lack', label: '기능 부족' },
    { value: 'complex_interface', label: '복잡한 인터페이스' },
    { value: 'frequent_errors', label: '잦은 오류 발생' },
    { value: 'expensive', label: '가격 부담' },
    { value: 'plan_misunderstanding', label: '플랜 내용 오인' },
    { value: 'other_system', label: '다른 시스템 도입 결정' },
    { value: 'support_issues', label: '고객 지원 불만족' },
    { value: 'custom', label: '직접 입력' }
  ];

  const handleReasonChange = (value, label) => {
    setRefundReason(value);
    setSelectedOption(label);
    setIsDropdownOpen(false);
  };

  const handleCustomReasonChange = (e) => {
    setCustomReason(e.target.value);
  };

  const toggleDropdown = () => {
    setIsDropdownOpen(!isDropdownOpen);
  };

  const handleRefundPolicy = () => {
    console.log('환불 규정 확인');
    navigate(`/dashboard/refund-policy/${id}`);
  };

  const handleSubmitRefund = async () => {
    const finalReason = refundReason === 'custom' ? customReason : selectedOption;
    console.log('환불 요청 제출:', finalReason);
    
    if (!finalReason || (refundReason === 'custom' && !customReason.trim())) {
      setDialogType(Dialog.TYPES.ERROR);
      setDialogMessage('환불 사유를 입력해주세요.');
      setDialogOpen(true);
      return;
    }
    
    try {
      setLoading(true);
      
      console.log('환불 요청 시작 - 결제 ID:', id);
      console.log('환불 사유:', finalReason);
      
      const response = await submitRefundRequest(id, finalReason);
      console.log('환불 요청 응답:', response);
      
      if (response && response.status === 'success') {
        setDialogType(Dialog.TYPES.SUCCESS);
        setDialogMessage('환불 요청이 성공적으로 제출되었습니다.');
        setDialogOpen(true);
      } else {
        const errorMessage = response?.message || response?.detail || '환불 요청 처리 중 오류가 발생했습니다.';
        setDialogType(Dialog.TYPES.ERROR);
        setDialogMessage(`환불 요청 오류: ${errorMessage}`);
        setDialogOpen(true);
      }
    } catch (error) {
      console.error('환불 요청 오류 상세:', error);
      // 오류 메시지를 다이얼로그에 표시
      setDialogType(Dialog.TYPES.ERROR);
      setDialogMessage(error.message || '환불 요청 처리 중 오류가 발생했습니다.');
      setDialogOpen(true);
    } finally {
      setLoading(false);
    }
  };

  // 다이얼로그 닫기 핸들러
  const handleCloseDialog = () => {
    setDialogOpen(false);
    
    // 성공적인 환불 요청 후 페이지 이동
    if (dialogType === Dialog.TYPES.SUCCESS) {
      navigate('/dashboard/payment');
    }
  };
  
  // 다이얼로그 확인 버튼 핸들러
  const handleDialogConfirm = () => {
    handleCloseDialog();
  };

  // 로딩 중인 경우
  if (loading) {
    return (
      <div className="refundRequestContainer">
        <h1 className="pageTitle">환불 요청하기</h1>
        <div className="loadingMessage">결제 정보를 불러오는 중...</div>
      </div>
    );
  }

  return (
    <div className="refundRequestContainer">
      <h1 className="pageTitle">환불 요청하기</h1>
      <p className="pageDescription">환불 요청이 승인 처리되면, 해당 플랜의 서비스 제공이 종료됩니다.</p>
      
      <div className="refundSection">
        <h2 className="sectionTitle">환불 대상</h2>
        <div className="infoBox">
          <p>{currentPaymentData.purchaseDate} - {currentPaymentData.plan} - {currentPaymentData.amount}</p>
        </div>
      </div>
      
      <div className="refundSection">
        <h2 className="sectionTitle">환불 예정</h2>
        <div className="infoBox">
          <p>{currentPaymentData.paymentMethod} 으로</p>
          <p>{currentPaymentData.refundAmount || currentPaymentData.amount} 환불 예정</p>
        </div>
      </div>
      
      <div className="refundSection">
        <h2 className="sectionTitle">환불 사유 입력</h2>
        <div className="customDropdownContainer">
          <div className="customDropdownHeader" onClick={toggleDropdown}>
            <span>{selectedOption}</span>
            <span className={`dropdownArrow ${isDropdownOpen ? 'open' : ''}`}>▼</span>
          </div>
          
          {isDropdownOpen && (
            <div className="customDropdownOptions">
              {reasonOptions.map((option) => (
                <div 
                  key={option.value} 
                  className="customDropdownOption"
                  onClick={() => handleReasonChange(option.value, option.label)}
                >
                  {option.label}
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* 직접 입력을 선택했을 때만 텍스트 영역 표시 */}
        {refundReason === 'custom' && (
          <div className="customReasonContainer">
            <textarea
              className="customReasonTextarea"
              placeholder="환불 사유를 입력해주세요"
              value={customReason}
              onChange={handleCustomReasonChange}
              rows={5}
            ></textarea>
          </div>
        )}
      </div>
      
      <div className="refundPolicyContainer" style={{ textAlign: 'left', paddingLeft: '0' }}>
        <button className="refundPolicyButton" onClick={handleRefundPolicy} style={{ marginLeft: '0' }}>
          환불 규정 확인하기
          <span className="arrowIcon">›</span>
        </button>
      </div>
      
      <div className="submitContainer">
        <button className="submitButton" onClick={handleSubmitRefund}>
          환불 요청 제출하기
        </button>
      </div>

      {/* 다이얼로그 컴포넌트 */}
      <Dialog 
        isOpen={dialogOpen} 
        type={dialogType} 
        message={dialogMessage}
        onClose={handleCloseDialog} 
        onConfirm={handleDialogConfirm} 
      />
    </div>
  );
};

export default RefundRequest; 